import { Fragment, ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { shops, overViewItems, revenue } from './OverViewItems'
import { ExternalLinkIcon } from '@/components/elements/Icon/ExternalLink'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { CompanyLinks } from '@/components/layouts/Company/CompanyLinks'
import { Head } from '@/components/layouts/Head'


const sectionTitle = {
  titleText: 'Company Profile',
  subTitleText: '会社概要',
}

export const OverViewPage = () => {
  return (
    <>
      <Head
        title={sectionTitle.subTitleText}
        description="トライアルホールディングスの会社情報です。商号、所在地、設立年月日などの基本情報を掲載しています。"
        ogp={{ type: 'article', url: 'company/overview/' }}
      />
      <OverViewContainer>
        <Spacer h={{ sp: 40, pc: 69 }} />
        <SectionTitle
          titleText={sectionTitle.titleText}
          subTitleText={sectionTitle.subTitleText}
          color="normal"
        />
        <Table>
          <tbody>
            {overViewItems.map(({ title, descriptions, url }) => (
              <tr key={title}>
                <th>{title}</th>
                <td>
                  {descriptions.map((description, index) => (
                    <Fragment key={`${description}${index}`}>
                    {typeof description === 'string' ? (
                      // descriptionが文字列の場合
                      <span>
                        {description}
                      </span>
                    ) : (
                      // descriptionがオブジェクトの場合
                      <PostItem>
                        {description.post && description.url ? (
                          // descriptionがリンクを持つオブジェクトの場合
                          <a href={description.url} target="_blank" rel="noreferrer noopener">
                            <span>{description.name} <ExternalLinkIcon size="small" /></span>
                          </a>
                        ) : (
                          // リンクを持たない場合
                          <DescriptionDiv className="description-container">
                            <div className="description-post">{description.post}</div>
                            <div className="description-name">{description.name}</div>
                          </DescriptionDiv>
                        )}
                      </PostItem>
                    )}
                    {index !== descriptions.length - 1 && <br />}
                  </Fragment>
                    // <Fragment key={`${description}${index}`}>
                    //   {url === undefined ? (
                    //     description
                    //   ) : (
                    //     <a href={url} target="_blank" rel="noreferrer noopener">
                    //       {description}
                    //       <ExternalLinkIcon size="small" />
                    //     </a>
                    //   )}
                    //   {index !== descriptions.length - 1 && <br />}
                    // </Fragment>
                  ))}
                </td>
              </tr>
            ))}
            <tr className={shouldHideBorder ? 'no-border' : ''}>
              <th>{revenue.title}</th>
              <td>{revenue.descriptions}</td>
            </tr>

            <tr>
              <th>{shops.title}</th>
              <td>
                {shops.descriptions.map((description, index) => (
                  <Fragment key={`${description}${index}`}>
                    {description}
                    {index !== shops.descriptions.length - 1 && <br />}
                  </Fragment>
                ))}
                <br />
                <img src={shops.src} alt={shops.title}></img>
                <p>
                  ※決算期を変更した2005年3月期(6ヵ月決算)、2009年3月期(11ヵ月20日決算)及び2021年6月期(3ヵ月10日決算)は除く。
                </p>
                <p>※売上高及び店舗数は各会計年度(末)時点</p>
              </td>
            </tr>
          </tbody>
        </Table>
        <CompanyLinks padding={{ pc: '130px 0 0' }} />
      </OverViewContainer>
    </>
  )
}
OverViewPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Corporate Information',
    subTitle: '企業情報',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '企業情報', path: '/company/' },
    { name: '会社概要', path: '' },
  ]
  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const OverViewContainer = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 0 auto;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 0 20px 73px;
  }
`

const Table = styled.table`
  width: 100%;
  margin-top: 108px;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 1.8px;
  text-align: left;

  tr {
    border-bottom: 1px solid ${theme.colors.grey};
  }

  th {
    width: 330px;
    padding: 32px 0;
    color: ${theme.colors.trialBlue};
    line-height: 30px;
  }

  td {
    padding: 32px 112px 32px 0;
    line-height: 34px;

    img {
      width: 100%;
      padding-top: 30px;
    }

    p {
      font-size: 11.5px;
      line-height: 1.5;
    }
  }

  a {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: ${theme.colors.trialBlue};
    text-decoration: underline;
    transition: color 0.3s ease-out;

    &:hover {
      color: ${theme.colors.hoverLightBlue};

      > svg {
        fill: ${theme.colors.hoverLightBlue};
      }
    }
  }

  .no-border {
    border: none !important;
  }

  @media screen and (max-width: 1047px) {
    margin-top: 34px;

    tr {
      display: flex;
      flex-direction: column;
      padding: 26px 0;
      row-gap: 10px;
    }

    th,
    td {
      width: 100%;
      padding: 0;
    }

    td {
      font-size: 15px;
      letter-spacing: 1.5px;
      line-height: 25px;

      img {
        padding-top: 10px;
      }
    }

    a {
      font-size: 16px;
      letter-spacing: 1.6px;
      line-height: 34px;
    }
  }
`

const shouldHideBorder = true

const DescriptionDiv = styled.div`
  @media screen and (min-width: 1048px) {
    /* PC画面用のスタイル */
    display: table;
    width: 100%;
    table-layout: fixed;
    .description-post {
      display: table-cell;
      white-space: nowrap;
      line-height: 1.0; /* 行間の設定 */
    }
    .description-name {
      display: table-cell;
      padding-left: -20%; /* 左側の余白を設定 */
      white-space: nowrap;
      line-height: 1.0; /* 行間の設定 */
    }
  }
  @media screen and (max-width: 1047px) {
    /* タブレット・スマホ画面用のスタイル */
    display: table;
    width: 100%;
    table-layout: fixed;
    .description-post {
      display: table-cell;
      word-break: break-word;
    }
    .description-name {
      display: table-cell;
      padding-left: -10%; /* 左側の余白を設定 */
      padding-right:3%;
      word-break: break-word;
    }
  }
`

const PostItem = styled.li`
  display: flex;
  gap: 20px;

  span:first-child {
    width: 400px;
  }

  span:last-child {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    gap: 10px;

    span::first-child {
      span: 90px;
    }
  }
`
